/**
 * JC_DS_ALL_PRICING_23. Strip only the EUR glyph from rendered storefront text.
 * Never reformat numbers, replace HTML, write cart data or assign visitors.
 * The pending Intelligems experiment leaves production on Control.
 */
(() => {
  if (window.jcPricing23) return;
  const configElement = document.getElementById('jc-pricing-23-config');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch {
    return;
  }
  if (config.currency !== 'EUR' || /\/(cart|checkout|checkouts)(\/|$)/.test(location.pathname)) return;

  const excluded = [
    'script', 'style', 'template', 'noscript', 'iframe', 'object', 'svg',
    'input', 'textarea', 'select', 'option', '[contenteditable]',
    '[data-jc-preserve-euro]', '[itemscope][itemtype*="schema.org"] meta',
    'cart-drawer-component', 'cart-drawer', 'cart-items-component',
    '.cart-drawer', '.cart-page', '#CartDrawer', '#cart-drawer',
    '#cart-notification', 'shopify-payment-terms', 'shopify-payment-button',
    '#preview-bar-iframe', '#intelligems-preview', '[data-intelligems-preview]',
  ].join(',');
  const changedText = new Map();
  const pendingRoots = new Set();
  let active = false;
  let queued = false;
  let suspended = false;

  function protectedElement(element) {
    return !element || Boolean(element.closest(excluded));
  }

  // Retain exact whitespace, separators and precision; only remove U+20AC.
  function withoutEuro(text) {
    return text.replace(/€(?=\s*[-−]?\s*\d)|(?<=\d[\d.,\s]*[-−]?)\s*€/g,
      (match) => match.replace('€', ''));
  }

  function restoreNode(node, saved) {
    // An app may have rendered a newer amount since our last pass.
    if (node.data === saved.rendered) node.data = saved.original;
    changedText.delete(node);
  }

  function updateText(node) {
    if (protectedElement(node.parentElement) || !node.data.includes('€')) return;
    const original = node.data;
    let rendered = withoutEuro(original);
    // Replo and app widgets can put the symbol and amount in sibling spans.
    if (rendered === original && /^\s*€\s*$/.test(original)) {
      const parent = node.parentElement;
      const context = parent.childElementCount ? parent : parent.parentElement;
      if (context && !protectedElement(context) && /\d/.test(context.textContent)) {
        rendered = original.replace('€', '');
      }
    }
    if (rendered === original) return;
    changedText.set(node, { original, rendered });
    node.data = rendered;
  }

  function scan(root) {
    if (!root.isConnected) return;
    if (root.nodeType === Node.TEXT_NODE) {
      updateText(root);
      return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE || protectedElement(root)) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          return protectedElement(node) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_SKIP;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });
    while (walker.nextNode()) updateText(walker.currentNode);
  }

  function observe() {
    if (active && !suspended && document.body) {
      observer.observe(document.body, { childList: true, characterData: true, subtree: true });
    }
  }

  function flush() {
    queued = false;
    if (!active || suspended) return;
    // Our own text writes should not schedule another pass.
    observer.disconnect();
    for (const [node, saved] of changedText) {
      if (!node.isConnected || protectedElement(node.parentElement)) restoreNode(node, saved);
    }
    const roots = [...pendingRoots];
    pendingRoots.clear();
    for (const root of roots) {
      if (!roots.some((other) => other !== root && other.contains(root))) scan(root);
    }
    observe();
  }

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'characterData') {
        // Include sibling symbols when a separately rendered amount changes.
        pendingRoots.add(record.target.parentElement);
      } else {
        pendingRoots.add(record.target);
      }
    }
    pendingRoots.delete(null);
    if (!queued) {
      queued = true;
      queueMicrotask(flush);
    }
  });

  function setArm(arm) {
    active = arm === 'variant';
    document.documentElement.dataset.jcPricing23 = arm;
    if (active) {
      pendingRoots.add(document.body);
      pendingRoots.delete(null);
      flush();
    } else {
      observer.disconnect();
      pendingRoots.clear();
      for (const [node, saved] of changedText) restoreNode(node, saved);
    }
  }

  // Deliberately separate theme QA from measured visitor assignment. No override
  // is read or persisted on a published theme, including old session values.
  function previewArm() {
    if (!config.allowPreview) return null;
    const value = new URLSearchParams(location.search).get('jc_pricing_23');
    try {
      if (value === 'auto') sessionStorage.removeItem('jc_pricing_23_preview');
      if (value === 'control' || value === 'variant') {
        sessionStorage.setItem('jc_pricing_23_preview', value);
        return value;
      }
      const saved = sessionStorage.getItem('jc_pricing_23_preview');
      return saved === 'control' || saved === 'variant' ? saved : null;
    } catch {
      return value === 'control' || value === 'variant' ? value : null;
    }
  }

  function resolveArm() {
    const preview = previewArm();
    if (preview) {
      setArm(preview);
      return;
    }
    try {
      const user = window.igData?.user;
      const experiment = user?.getExperiments().find((item) => item.id === config.experimentId);
      const group = experiment ? user.getTestGroup(config.experimentId) : null;
      setArm(group?.id === config.variantId && group.isControl === false ? 'variant' : 'control');
    } catch {
      setArm('control');
    }
  }

  function suspend() {
    suspended = true;
    observer.disconnect();
    pendingRoots.clear();
  }

  function resume() {
    suspended = false;
    resolveArm();
  }

  window.jcPricing23 = { refresh: resolveArm };
  window.addEventListener('ig:ready', resolveArm);
  window.addEventListener('ig:variationAssignments', resolveArm);
  window.addEventListener('pagehide', suspend);
  window.addEventListener('pageshow', resume);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', resolveArm, { once: true });
  } else {
    resolveArm();
  }
})();
